import {useI18n} from '@logistics-frontend/core/i18n'
import clsx from 'clsx'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {useCountryInfoContext} from '../../country-info'
import {makeStyles, Theme} from '../../theme'
import {ArrowButton} from './ArrowButton'
import {Day} from './Day'
import {DayInfo, getMonthByDate, getMonthDays, getYearByDate, isAfter, isBefore, isSame, TimeAccuracy} from './utils'
import {YearsList} from './YearsList'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 280,
    ...theme.go.mixins.typography.Body,
  },
  header: {
    padding: '9px 8px 9px 8px',
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.go.schemes.colors.system.TextMain,
    ...theme.go.mixins.typography.Headline1,
  },
  body: {
    height: 280,
  },
  monthControl: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'capitalize',

    '& $arrowRight': {
      marginLeft: 4,
    },
  },
  monthStepper: {
    display: 'flex',
    alignItems: 'center',
  },
  tableRow: {
    display: 'flex',
  },
  arrowRight: {
    marginLeft: 8,
  },
  tableHead: {
    ...theme.go.mixins.typography.Subhead,
    color: theme.go.schemes.colors.system.TextMinor,

    '& $tableCell': {
      cursor: 'default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderBottom: 'none',
    },
  },
  tableCell: {
    width: 40,
    height: 40,
    borderBottom: `4px solid ${theme.go.schemes.colors.system.BgMain}`,
    userSelect: 'none',
  },
}))

export type SelectedRange = [string | undefined, string | undefined]

export type CalendarProps = {
  value?: string
  defaultValue?: string
  selectedRange?: SelectedRange
  onDayClick?: (date: string) => void
  isDisabledDate?: (date: string) => boolean
  noSelection?: boolean
  classes?: {
    root?: string
    header?: string
    monthControl?: string
    monthStepper?: string
    body?: string
    tableHead?: string
    tableCell?: string
    tableRow?: string
    arrowRight?: string
  }
  years?: number[]
}

const isEmptySelectedRange = (selectedRange?: SelectedRange) => !selectedRange || !selectedRange[0] || !selectedRange[1]

const getCalendarDaySelectionProps = (
  dayInfo: DayInfo,
  _: string,
  selectedRange?: SelectedRange,
  accuracy: TimeAccuracy = 'day',
) => ({
  firstInRange: !isEmptySelectedRange(selectedRange) && isSame(dayInfo.date, selectedRange[0], accuracy),
  lastInRange: !isEmptySelectedRange(selectedRange) && isSame(dayInfo.date, selectedRange[1], accuracy),
  inRange:
    !isEmptySelectedRange(selectedRange) &&
    isAfter(dayInfo.date, selectedRange[0], accuracy) &&
    isBefore(dayInfo.date, selectedRange[1], accuracy),
  selected: selectedRange.some(rangeDate => rangeDate && isSame(dayInfo.date, rangeDate, accuracy)),
})

export function Calendar({
  value,
  selectedRange,
  noSelection,
  onDayClick,
  classes,
  isDisabledDate,
  defaultValue,
  years,
}: CalendarProps) {
  const {root, header, monthControl, monthStepper, body, tableHead, tableCell, tableRow, arrowRight} = useStyles()

  const now = new Date().toISOString()
  const parsedMonth = getMonthByDate(value || defaultValue || now)
  const parsedYear = getYearByDate(value || defaultValue || now)
  const country = useCountryInfoContext()
  const countryCode = country?.id?.alpha3 || ''
  const isSundayFirstDay = ['isr'].includes(countryCode)

  const [month, setMonth] = useState(parsedMonth)
  const [year, setYear] = useState(parsedYear)
  const [showYears, toggleYears] = useState(false)
  const days = getMonthDays(month, year, isSundayFirstDay)
  const t = useI18n()

  const weekDays = [
    t('calendar.monday', 'пн'),
    t('calendar.tuesday', 'вт'),
    t('calendar.wednesday', 'ср'),
    t('calendar.thursday', 'чт'),
    t('calendar.friday', 'пт'),
    t('calendar.saturday', 'сб'),
  ]

  if (isSundayFirstDay) {
    weekDays.unshift(t('calendar.sunday', 'вс'))
  } else {
    weekDays.push(t('calendar.sunday', 'вс'))
  }

  const months = [
    t('calendar.january', 'Январь'),
    t('calendar.february', 'Февраль'),
    t('calendar.march', 'Март'),
    t('calendar.april', 'Апрель'),
    t('calendar.may', 'Май'),
    t('calendar.june', 'Июнь'),
    t('calendar.july', 'Июль'),
    t('calendar.august', 'Август'),
    t('calendar.september', 'Сентябрь'),
    t('calendar.october', 'Октябрь'),
    t('calendar.november', 'Ноябрь'),
    t('calendar.december', 'Декабрь'),
  ]

  useEffect(() => {
    const month = noSelection ? (parsedMonth + 1 === 12 ? 0 : parsedMonth + 1) : parsedMonth
    setMonth(month)
  }, [parsedMonth, noSelection])

  useEffect(() => {
    setYear(parsedYear)
  }, [parsedYear])

  const incMonth = useCallback(() => {
    if (month + 1 < 12) {
      setMonth(month + 1)
    } else {
      setMonth(0)
      setYear(year + 1)
    }
  }, [month, setMonth, year, setYear])

  const decMonth = useCallback(() => {
    if (month - 1 >= 0) {
      setMonth(month - 1)
    } else {
      setMonth(11)
      setYear(year - 1)
    }
  }, [month, setMonth, year, setYear])

  const onYearsClick = useCallback(() => {
    toggleYears(!showYears)
  }, [showYears, toggleYears])

  const onYearClick = useCallback(
    (year: number) => {
      setYear(year)
      toggleYears(false)
    },
    [setYear, toggleYears],
  )

  const dayClasses = useMemo(
    () => ({
      root: clsx(tableCell, classes?.tableCell),
    }),
    [classes?.tableCell, tableCell],
  )

  return (
    <div className={clsx(root, classes?.root)}>
      <div className={clsx(header, classes?.header)}>
        <span className={clsx(monthControl, classes?.monthControl)}>
          <span>{`${months[month]} ${year}`}</span>
          <ArrowButton
            className={clsx(arrowRight, classes?.arrowRight)}
            direction={showYears ? undefined : 'right'}
            onClick={onYearsClick}
          />
        </span>
        <span className={clsx(monthStepper, classes?.monthStepper)}>
          <ArrowButton direction="left" onClick={decMonth} />
          <ArrowButton className={clsx(arrowRight, classes?.arrowRight)} direction="right" onClick={incMonth} />
        </span>
      </div>
      <div className={clsx(body, classes?.body)}>
        {showYears ? (
          <YearsList onItemClick={onYearClick} year={year} years={years} />
        ) : (
          <>
            <div className={clsx(tableRow, tableHead, classes?.tableRow, classes?.tableHead)}>
              {weekDays.map(day => (
                <div key={day} className={clsx(tableCell, classes?.tableCell)}>
                  {day}
                </div>
              ))}
            </div>
            {days.map((week, r) => (
              <div key={r} className={clsx(tableRow, classes?.tableRow)}>
                {week.map((dayInfo, c) => (
                  <Day
                    key={`${c}_${r}`}
                    classes={dayClasses}
                    disabled={isDisabledDate ? isDisabledDate(dayInfo.date) : false}
                    onClick={onDayClick}
                    {...(!noSelection && getCalendarDaySelectionProps(dayInfo, value, selectedRange))}
                    {...dayInfo}
                  />
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}
